import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Building2, Plus, Pencil, Trash2, ArrowLeft, LogOut, 
  Save, X, Eye, EyeOff, GripVertical
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBankAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const { authenticated, currentUser, loading: authLoading, logout, getAuthHeader } = useAdminAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    bank_name: '',
    account_number: '',
    account_name: '',
    bank_code: '',
    is_active: true,
    order: 0
  });

  useEffect(() => {
    if (!authLoading && !authenticated) {
      navigate('/admin/login');
    }
  }, [authenticated, authLoading, navigate]);

  useEffect(() => {
    if (authenticated && currentUser) {
      if (currentUser.role !== 'super_admin') {
        toast.error('Only super admin can manage bank accounts');
        navigate('/admin/dashboard');
        return;
      }
      fetchAccounts();
    }
  }, [authenticated, currentUser]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/bank-accounts`, {
        headers: getAuthHeader()
      });
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      bank_name: '',
      account_number: '',
      account_name: '',
      bank_code: '',
      is_active: true,
      order: accounts.length
    });
    setShowForm(false);
    setEditingAccount(null);
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      bank_name: account.bank_name || '',
      account_number: account.account_number || '',
      account_name: account.account_name || '',
      bank_code: account.bank_code || '',
      is_active: account.is_active !== false,
      order: account.order || 0
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.bank_name || !formData.account_number || !formData.account_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingAccount) {
        await axios.put(`${API}/admin/bank-accounts/${editingAccount._id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Bank account updated');
      } else {
        await axios.post(`${API}/admin/bank-accounts`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Bank account added');
      }
      resetForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank account');
    }
  };

  const handleDelete = async (accountId, bankName) => {
    if (!window.confirm(`Delete ${bankName} account? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/bank-accounts/${accountId}`, {
        headers: getAuthHeader()
      });
      toast.success('Bank account deleted');
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    }
  };

  const toggleActive = async (account) => {
    try {
      await axios.put(`${API}/admin/bank-accounts/${account._id}`, {
        is_active: !account.is_active
      }, {
        headers: getAuthHeader()
      });
      toast.success(`Account ${account.is_active ? 'deactivated' : 'activated'}`);
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to update account status');
    }
  };

  if (authLoading || !authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-bank-accounts-page">
      <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Bank Accounts</h1>
            <p className="text-sm text-gray-500">Manage payment accounts displayed at checkout</p>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-account-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Account
          </Button>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {editingAccount ? <Pencil className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                {editingAccount ? 'Edit Bank Account' : 'Add New Bank Account'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Bank Name *</label>
                    <Input
                      name="bank_name"
                      value={formData.bank_name}
                      onChange={handleInputChange}
                      placeholder="e.g., First Bank, GTBank"
                      required
                      data-testid="bank-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Account Number *</label>
                    <Input
                      name="account_number"
                      value={formData.account_number}
                      onChange={handleInputChange}
                      placeholder="10-digit account number"
                      required
                      data-testid="account-number-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Account Name *</label>
                    <Input
                      name="account_name"
                      value={formData.account_name}
                      onChange={handleInputChange}
                      placeholder="Account holder name"
                      required
                      data-testid="account-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Bank Code (optional)</label>
                    <Input
                      name="bank_code"
                      value={formData.bank_code}
                      onChange={handleInputChange}
                      placeholder="e.g., 011, 058"
                      data-testid="bank-code-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Display Order</label>
                    <Input
                      name="order"
                      type="number"
                      value={formData.order}
                      onChange={handleInputChange}
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="rounded"
                      />
                      <span className="text-sm">Active (show at checkout)</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" data-testid="save-account-btn">
                    <Save className="h-4 w-4 mr-2" />
                    {editingAccount ? 'Update Account' : 'Add Account'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    <X className="h-4 w-4 mr-2" /> Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Accounts List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Payment Accounts ({accounts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
              </div>
            ) : accounts.length === 0 ? (
              <div className="text-center py-8 text-gray-600">
                No bank accounts added yet. Click "Add Account" to add your first payment account.
              </div>
            ) : (
              <div className="space-y-3">
                {accounts.map((account) => (
                  <div
                    key={account._id}
                    className={`p-4 border rounded-lg flex items-center justify-between ${
                      account.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <GripVertical className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{account.bank_name}</p>
                        <p className="text-sm text-gray-600">{account.account_number}</p>
                        <p className="text-sm text-gray-500">{account.account_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        account.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {account.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={() => toggleActive(account)}
                        className={`p-2 rounded-full transition-colors ${
                          account.is_active
                            ? 'bg-green-100 text-green-600 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={account.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {account.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleEdit(account)}
                        className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(account._id, account.bank_name)}
                        className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">How Bank Accounts Work</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Active accounts will be displayed to customers during checkout</li>
            <li>• Customers can see account details and make bank transfers</li>
            <li>• Use the order field to control display sequence</li>
            <li>• Only Super Admin can add, edit, or remove bank accounts</li>
          </ul>
        </div>
    </div>
  );
};

export default AdminBankAccounts;
